import { useState, useEffect } from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { cn } from "../lib/utils"

const TOAST_TIMEOUT = 5000

export function ToastRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()
  const [visible, setVisible] = useState(true)

  const message = element.text ? String(resolveSymbol(element.text) ?? "") : ""
  const variant = element.variant ?? "default"

  useEffect(() => {
    const timer = setTimeout(() => {
      setVisible(false)
      dispatchEvent(element.id, "dismiss", { reason: "timeout" })
    }, TOAST_TIMEOUT)
    return () => clearTimeout(timer)
  }, [element.id, dispatchEvent])

  const handleDismiss = () => {
    setVisible(false)
    dispatchEvent(element.id, "dismiss", { reason: "manual" })
  }

  if (!visible) return null

  const variantClasses: Record<string, string> = {
    default: "border bg-background text-foreground",
    success: "border-green-500/50 bg-green-50 text-green-900 dark:bg-green-950 dark:text-green-100",
    error: "border-destructive/50 bg-red-50 text-red-900 dark:bg-red-950 dark:text-red-100",
    warning: "border-yellow-500/50 bg-yellow-50 text-yellow-900 dark:bg-yellow-950 dark:text-yellow-100",
  }

  return (
    <div
      role="status"
      className={cn(
        "pointer-events-auto flex w-full items-center justify-between gap-2 rounded-lg border p-4 shadow-lg transition-all",
        variantClasses[variant] ?? variantClasses.default,
        element.className,
      )}
      data-testid={`toast-${element.id}`}
    >
      <span className="text-sm font-semibold">{message}</span>
      <button
        type="button"
        className="inline-flex shrink-0 items-center justify-center rounded-md p-1 text-foreground/50 opacity-70 transition-opacity hover:opacity-100 focus:outline-none"
        onClick={handleDismiss}
        aria-label="Dismiss"
      >
        <span aria-hidden="true" className="text-lg leading-none">&times;</span>
      </button>
    </div>
  )
}
